import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useMultiTenant } from '@/hooks/useMultiTenant';
import { useToast } from '@/hooks/use-toast';
import { useStableAuth } from '@/hooks/useStableAuth';

export type ReconciliationStatus = 'pending' | 'reconciled' | 'ignored' | 'manual';

export interface BankTransaction {
  id: string;
  company_id: string;
  financial_account_id: string;
  bank_integration_id: string;
  external_id: string;
  transaction_date: string;
  description: string;
  value: number;
  transaction_type: 'credit' | 'debit';
  balance_after?: number;
  category?: string;
  counterpart_name?: string;
  counterpart_document?: string;
  reconciliation_status: ReconciliationStatus;
  reconciled_at?: string;
  reconciled_by?: string;
  ignore_reason?: string;
  receivable_payment_id?: string;
  payable_payment_id?: string;
  synced_at: string;
  created_at: string;
  updated_at: string;
  // Joined data for reconciled items
  system_description?: string;
  system_entity_name?: string;
}

interface UseBankTransactionsOptions {
  financialAccountId?: string;
  status?: ReconciliationStatus;
  startDate?: string;
  endDate?: string;
  transactionType?: 'credit' | 'debit';
}

export const useBankTransactions = (options: UseBankTransactionsOptions = {}) => {
  const { companyId } = useMultiTenant();
  const { userId } = useStableAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { financialAccountId, status, startDate, endDate, transactionType } = options;

  const query = useQuery({
    queryKey: ['bank-transactions', companyId, financialAccountId, status, startDate, endDate, transactionType],
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      if (!companyId) throw new Error('Company ID não disponível');

      let queryBuilder = supabase
        .from('bank_transactions')
        .select(`
          *,
          receivable_payments:receivable_payment_id(
            id,
            payment_value,
            accounts_receivable:receivable_id(
              description,
              customers:customer_id(name)
            )
          ),
          payable_payments:payable_payment_id(
            id,
            payment_value,
            accounts_payable:payable_id(
              description,
              suppliers:supplier_id(name)
            )
          )
        `)
        .eq('company_id', companyId)
        .order('transaction_date', { ascending: false });

      if (financialAccountId) {
        queryBuilder = queryBuilder.eq('financial_account_id', financialAccountId);
      }

      if (status) {
        queryBuilder = queryBuilder.eq('reconciliation_status', status);
      }

      if (startDate) {
        queryBuilder = queryBuilder.gte('transaction_date', startDate);
      }

      if (endDate) {
        queryBuilder = queryBuilder.lte('transaction_date', endDate);
      }

      if (transactionType) {
        queryBuilder = queryBuilder.eq('transaction_type', transactionType);
      }

      const { data, error } = await queryBuilder;

      if (error) throw error;

      // Map joined data into flat fields for easy consumption
      return (data || []).map((tx: any) => {
        const rec = tx.receivable_payments;
        const pay = tx.payable_payments;
        
        let system_description: string | undefined;
        let system_entity_name: string | undefined;

        if (rec?.accounts_receivable) {
          system_description = rec.accounts_receivable.description;
          system_entity_name = rec.accounts_receivable.customers?.name;
        } else if (pay?.accounts_payable) {
          system_description = pay.accounts_payable.description;
          system_entity_name = pay.accounts_payable.suppliers?.name;
        }

        return {
          id: tx.id,
          company_id: tx.company_id,
          financial_account_id: tx.financial_account_id,
          bank_integration_id: tx.bank_integration_id,
          external_id: tx.external_id,
          transaction_date: tx.transaction_date,
          description: tx.description,
          value: tx.value,
          transaction_type: tx.transaction_type,
          balance_after: tx.balance_after,
          category: tx.category,
          counterpart_name: tx.counterpart_name,
          counterpart_document: tx.counterpart_document,
          reconciliation_status: tx.reconciliation_status,
          reconciled_at: tx.reconciled_at,
          reconciled_by: tx.reconciled_by,
          ignore_reason: tx.ignore_reason,
          receivable_payment_id: tx.receivable_payment_id,
          payable_payment_id: tx.payable_payment_id,
          synced_at: tx.synced_at,
          created_at: tx.created_at,
          updated_at: tx.updated_at,
          system_description,
          system_entity_name,
        } as BankTransaction;
      });
    },
    enabled: !!companyId,
    placeholderData: keepPreviousData,
  });

  // Mutation para conciliar transação (R5-11: vínculo bidirecional)
  const reconcileMutation = useMutation({
    mutationFn: async ({
      transactionId,
      receivablePaymentId,
      payablePaymentId,
    }: {
      transactionId: string;
      receivablePaymentId?: string;
      payablePaymentId?: string;
    }) => {
      const { error } = await supabase
        .from('bank_transactions')
        .update({
          reconciliation_status: 'reconciled',
          reconciled_at: new Date().toISOString(),
          reconciled_by: userId,
          receivable_payment_id: receivablePaymentId || null,
          payable_payment_id: payablePaymentId || null,
        })
        .eq('id', transactionId);

      if (error) throw error;

      // R5-11: Atualizar o pagamento com o bank_transaction_id (vínculo bidirecional)
      // F2 (D-140C-iii.1): payable_payments ganhou a coluna na Peça 1 — dois lados
      if (receivablePaymentId) {
        await supabase
          .from('receivable_payments')
          .update({ bank_transaction_id: transactionId })
          .eq('id', receivablePaymentId);
      }
      if (payablePaymentId) {
        await supabase
          .from('payable_payments')
          .update({ bank_transaction_id: transactionId })
          .eq('id', payablePaymentId);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bank-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['system-payments'] });
      queryClient.invalidateQueries({ queryKey: ['accounts-receivable'] });
      queryClient.invalidateQueries({ queryKey: ['accounts-payable'] });
      queryClient.invalidateQueries({ queryKey: ['revenues'] });
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['financial-accounts'] });
      queryClient.invalidateQueries({ queryKey: ['financial-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['stats'] });
      toast({ title: 'Transação conciliada com sucesso' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao conciliar transação',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Mutation para ignorar transação
  const ignoreMutation = useMutation({
    mutationFn: async ({
      transactionId,
      reason,
    }: {
      transactionId: string;
      reason: string;
    }) => {
      const { error } = await supabase
        .from('bank_transactions')
        .update({
          reconciliation_status: 'ignored',
          reconciled_at: new Date().toISOString(),
          reconciled_by: userId,
          ignore_reason: reason,
        })
        .eq('id', transactionId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bank-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['revenues'] });
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['financial-accounts'] });
      queryClient.invalidateQueries({ queryKey: ['financial-dashboard'] });
      toast({ title: 'Transação ignorada' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao ignorar transação',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Mutation para desvincular transação — RPC atômica (I133 B4): deleta
  // payments (principal + ajuste + adicionais R5-5), limpa espelhos
  // revenues/expenses (RCVPMT-/PAYPMT-), recalcula títulos e reseta a tx.
  const unlinkMutation = useMutation({
    // ESTORNO-AUDITORIA (I.160, E9): motivo obrigatório; cada baixa vai ao histórico de estornos e a linha fica registrada
    mutationFn: async ({ id: transactionId, reason }: { id: string; reason: string }) => {
      const { data, error } = await supabase.rpc('unlink_bank_transaction_atomic', {
        p_bank_transaction_id: transactionId,
        p_reason: reason,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bank-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['system-payments'] });
      queryClient.invalidateQueries({ queryKey: ['accounts-receivable'] });
      queryClient.invalidateQueries({ queryKey: ['accounts-payable'] });
      queryClient.invalidateQueries({ queryKey: ['revenues'] });
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['financial-accounts'] });
      queryClient.invalidateQueries({ queryKey: ['financial-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['stats'] });
      toast({ title: 'Conciliação desfeita', description: 'As baixas ligadas foram estornadas e guardadas no histórico.' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao desfazer conciliação',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Cálculos
  const transactions = query.data || [];
  const pendingCount = transactions.filter(t => t.reconciliation_status === 'pending').length;
  const reconciledCount = transactions.filter(t => t.reconciliation_status === 'reconciled').length;
  const ignoredCount = transactions.filter(t => t.reconciliation_status === 'ignored').length;

  const totalCredits = transactions
    .filter(t => t.transaction_type === 'credit')
    .reduce((sum, t) => sum + t.value, 0);

  const totalDebits = transactions
    .filter(t => t.transaction_type === 'debit')
    .reduce((sum, t) => sum + t.value, 0);

  return {
    transactions,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
    
    // Mutations
    reconcile: reconcileMutation.mutate,
    ignore: ignoreMutation.mutate,
    unlink: unlinkMutation.mutate,
    isReconciling: reconcileMutation.isPending,
    isIgnoring: ignoreMutation.isPending,
    isUnlinking: unlinkMutation.isPending,
    
    // Contadores
    pendingCount,
    reconciledCount,
    ignoredCount,
    totalCredits,
    totalDebits,
  };
};
